import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import BrandName from '../components/BrandName';

class Login extends Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      rememberMe: false,
      errors: {},
      isSubmitting: false,
      loginError: null,
    };
  }

  componentDidMount() {
    // Redirect if already authenticated
    if (this.context.isAuthenticated) {
      window.location.href = '/jobs';
    }
  }

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      [name]: type === 'checkbox' ? checked : value,
      errors: { ...this.state.errors, [name]: '' },
    });
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter' && !this.state.isSubmitting) {
      e.preventDefault();
      this.handleSubmit();
    }
  };

  validateForm = () => {
    const { email, password } = this.state;
    const errors = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    return errors;
  };

  handleSubmit = async () => {
    // Validate form
    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }
  
    // Prevent double-submission (good UX)
    if (this.state.isSubmitting) {
      return;
    }
  
    // Start submission
    this.setState({ isSubmitting: true, loginError: null });
  
    const { email, password } = this.state;
    const result = await this.context.login(email, password);
  
    if (result.success) {
      window.location.href = '/jobs';
    } else {
      this.setState({ 
        isSubmitting: false,
        loginError: result.error || 'Invalid email or password'
      });
    }
  };

  render() {
    const { email, password, rememberMe, errors, isSubmitting, loginError } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card animate-fade-in">
            {/* Logo */}
            <div className="flex justify-center mb-6 sm:mb-8">
              <img 
                src="/logo.png" 
                alt="PlansSource Logo" 
                className="w-20 h-20 sm:w-32 sm:h-32 object-contain"
              />
            </div>

            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
                Welcome back
              </h1>
              <p className="text-sm sm:text-base text-slate-500">
                Sign in to your <BrandName /> account
              </p>
            </div>

            {/* Error Alert */}
            {loginError && (
              <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm">
                {loginError}
              </div>
            )}

            {/* Login Form */}
            <form 
              className="space-y-4 sm:space-y-6" 
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                return false;
              }}
            >
              {/* Email Field */}
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={this.handleChange}
                  onKeyPress={this.handleKeyPress}
                  className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="error-text">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label 
                  htmlFor="password" 
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={this.handleChange}
                  onKeyPress={this.handleKeyPress}
                  className={`input-field ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                />
                {errors.password && (
                  <p className="error-text">{errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={this.handleChange}
                    className="w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                    disabled={isSubmitting}
                  />
                  <span className="ml-2 text-sm text-slate-600">Remember me</span>
                </label>
                
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
                <button
                type="button"
                disabled={isSubmitting}
                className="btn-primary w-full min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={this.handleSubmit}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner size="small" />
                    <span className="ml-2">Signing in...</span>
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

export default Login;